/**
 * Match Flow View
 *
 * Main container component for the match flow player experience.
 * Renders the current node and flow progress.
 */

import { getTeamNames } from "@rivaled/shared";
import { AlertCircle, Check, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useMatchFlow } from "../hooks/useMatchFlow";
import { MatchLobbyLayout } from "../layout/MatchLobbyLayout";

function toContextValueString(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value == null) return null;
  return JSON.stringify(value);
}

type MatchFlowViewProps = {
  matchId: string;
  onComplete?: (context: Record<string, unknown>) => void;
  onFailed?: (error: string) => void;
};

export function MatchFlowView({ matchId, onComplete, onFailed }: MatchFlowViewProps) {
  const {
    flowState,
    currentNode,
    isConnected,
    isLoading,
    error,
    timeRemaining,
    progress,
    submitAction,
  } = useMatchFlow({ matchId });

  // Handle completion/failure callbacks via effect (not during render)
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);
  onCompleteRef.current = onComplete;
  onFailedRef.current = onFailed;

  useEffect(() => {
    if (flowState?.status === "completed" && onCompleteRef.current) {
      onCompleteRef.current(flowState.context);
    }
    if (flowState?.status === "failed" && onFailedRef.current && error) {
      onFailedRef.current(error);
    }
  }, [flowState?.status, flowState?.context, error]);

  // Loading state — skeleton matching layout structure
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        {/* Progress bar skeleton */}
        <div className="h-12 w-full rounded-lg bg-surface-800 animate-pulse" />
        {/* Content card skeleton */}
        <div className="rounded-xl bg-surface-800 h-64 animate-pulse" />
        {/* Player grid skeleton */}
        <div className="bg-surface-800 rounded-lg p-3">
          <div className="h-4 w-16 rounded bg-surface-700 animate-pulse mb-3" />
          <div className="grid grid-cols-2 @sm:grid-cols-4 gap-2">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-10 rounded-md bg-surface-700 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No flow state — flow data expired or was never created
  if (!flowState) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="h-16 w-16 rounded-full bg-surface-800 flex items-center justify-center">
          <AlertCircle className="h-8 w-8 text-surface-500" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Match Setup Expired</h2>
        <p className="text-text-secondary text-center max-w-sm">
          The match setup session has expired or could not be found. This match may need to be
          restarted.
        </p>
      </div>
    );
  }

  // Flow completed — show summary of decisions
  if (flowState.status === "completed") {
    const ctx = flowState.context;
    const completedTeams = ctx.teams as Record<string, number> | undefined;
    const completedTeamNames = completedTeams ? getTeamNames(ctx.matchId, ctx.teamCount) : [];
    const gameMode = ctx.selectedGameMode as { name?: string } | undefined;
    const sides = ctx.selectedSides as Record<string, string> | undefined;
    const picks = ctx.characterPicks as Record<string, string> | undefined;
    const bans = ctx.bannedCharacters as string[] | undefined;

    const summaryItems: { label: string; value: string }[] = [];
    if (ctx.gameName) summaryItems.push({ label: "Game", value: String(ctx.gameName) });
    if (gameMode?.name) summaryItems.push({ label: "Mode", value: gameMode.name });
    if (completedTeamNames.length > 0) {
      summaryItems.push({ label: "Teams", value: completedTeamNames.join(" vs ") });
    }
    if (sides && Object.keys(sides).length > 0) {
      const sideEntries = Object.entries(sides)
        .map(([team, side]) => `${completedTeamNames[Number(team)] ?? `Team ${team}`}: ${side}`)
        .join(", ");
      summaryItems.push({ label: "Sides", value: sideEntries });
    }
    if (bans && bans.length > 0) {
      summaryItems.push({ label: "Bans", value: `${bans.length} characters banned` });
    }
    if (picks && Object.keys(picks).length > 0) {
      summaryItems.push({
        label: "Picks",
        value: `${Object.keys(picks).length} characters picked`,
      });
    }
    const lobbyCode = toContextValueString(ctx.lobbyCode);
    if (lobbyCode) summaryItems.push({ label: "Lobby Code", value: lobbyCode });

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-6">
        <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center">
          <Check className="h-8 w-8 text-green-500" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Match Setup Complete</h2>
        {summaryItems.length > 0 && (
          <div className="w-full max-w-md rounded-xl bg-surface-800 border border-surface-700/40 p-4">
            <div className="flex flex-col gap-3">
              {summaryItems.map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-surface-500 font-medium">
                    {item.label}
                  </span>
                  <span className="text-sm font-display font-semibold text-text-primary">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Flow failed
  if (flowState.status === "failed") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="h-16 w-16 rounded-full bg-error-500/20 flex items-center justify-center">
          <X className="h-8 w-8 text-error-500" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Match Setup Failed</h2>
        <p className="text-error-400">{error || "An error occurred during match setup"}</p>
      </div>
    );
  }

  // Flow cancelled
  if (flowState.status === "cancelled") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="h-16 w-16 rounded-full bg-surface-600 flex items-center justify-center">
          <X className="h-8 w-8 text-text-secondary" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary">Match Cancelled</h2>
        <p className="text-text-secondary">The match setup was cancelled</p>
      </div>
    );
  }

  // Active flow — delegate to adaptive lobby layout
  return (
    <MatchLobbyLayout
      players={flowState.players}
      context={flowState.context}
      currentNode={currentNode}
      progress={progress}
      timeRemaining={timeRemaining}
      onAction={submitAction}
      isConnected={isConnected}
    />
  );
}
